/**
 * API client.
 *
 * The access token is held in memory only (spec §4) — never localStorage, so
 * an XSS bug cannot read it back out. It is lost on reload; the refresh cookie
 * restores it via bootstrap() below.
 */

export type Role = 'SUPER_ADMIN' | 'SCHOOL_ADMIN' | 'COORDINATOR' | 'TEACHER';
export type Language = 'PS' | 'DA' | 'EN';
export type Quality = 'HD' | 'SD';

export interface Me {
  id: string;
  email: string;
  role: Role;
  schoolId: string | null;
  schoolName: string | null;
}

export interface CatalogBook {
  id: string;
  title: string;
  order: number;
  hasCover: boolean;
  videoCount: number;
}
export interface CatalogSubject {
  id: string;
  name: string;
  order: number;
  books: CatalogBook[];
}
export interface CatalogGrade {
  id: string;
  name: string;
  order: number;
  subjects: CatalogSubject[];
}

export interface BookVideo {
  id: string;
  title: string;
  order: number;
  durationSec: number | null;
  languages: Language[];
  qualities: Quality[];
}
export interface BookDetail {
  id: string;
  title: string;
  grade: { id: string; name: string };
  subject: { id: string; name: string };
  videos: BookVideo[];
}

export interface VideoDetail {
  id: string;
  title: string;
  order: number;
  durationSec: number | null;
  /**
   * Whether this viewer may download this lesson (Phase 4). Drives whether the
   * button renders — the media routes enforce it independently, so a client
   * that ignores this gains nothing.
   */
  canDownload: boolean;
  book: { id: string; title: string };
  subject: { id: string; name: string };
  grade: { id: string; name: string };
  variants: { language: Language; quality: Quality; sizeBytes: string }[];
}

// ---- Super-admin (Phase 2) --------------------------------------------------

export type SchoolStatus = 'ACTIVE' | 'SUSPENDED' | 'EXPIRED';

export interface AdminSchool {
  id: string;
  name: string;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  status: SchoolStatus;
  licenseEnd: string | null;
  seatLimit: number | null;
  activeUsers: number;
  _count: { users: number; downloads: number; entitlements: number };
}

/** One grant. subjectId null = every subject in the grade. */
export interface EntitlementEntry {
  gradeId: string;
  subjectId?: string | null;
}

export interface AdminEntitlement extends EntitlementEntry {
  id: string;
  gradeName: string;
  subjectName: string | null;
}

/** The full grade->subject tree, unfiltered — super admin sees everything. */
export interface AdminGrade {
  id: string;
  name: string;
  order: number;
  subjects: { id: string; name: string; order: number }[];
}

export interface CreateSchoolInput {
  name: string;
  contactName?: string;
  phone?: string;
  email?: string;
  seatLimit?: number;
  licenseEnd?: string;
  entitlements?: EntitlementEntry[];
  admin?: { email: string; password: string };
}

// ---- Super-admin usage views (Phase 7) --------------------------------------

/** One row of the per-school overview table. */
export interface UsageSchoolRow {
  id: string;
  name: string;
  status: SchoolStatus;
  licenseEnd: string | null;
  /** Whole days until the licence ends. Negative = expired. null = no expiry. */
  daysToExpiry: number | null;
  seatLimit: number | null;
  seatsUsed: number;
  entitlementCount: number;
  admins: number;
  coordinators: number;
  teachers: number;
  suspendedUsers: number;
  /** Most recent login across the school's users. */
  lastActivityAt: string | null;
  views: number;
  downloads: number;
}

export interface UsageTotals {
  schools: number;
  activeSchools: number;
  suspendedSchools: number;
  expiredSchools: number;
  expiringWithin30Days: number;
  expiredLicences: number;
  seatsUsed: number;
  seatsLicensed: number;
  admins: number;
  coordinators: number;
  teachers: number;
  views: number;
  downloads: number;
}

export interface UsageOverview {
  windowDays: number | null;
  totals: UsageTotals;
  schools: UsageSchoolRow[];
}

/** One school's slice of a content row. */
export interface UsageContentSchool {
  schoolId: string;
  schoolName: string;
  views: number;
  downloads: number;
}

export interface UsageContentItem {
  videoFileId: string;
  /** null when the lesson has since been deleted; the counts still stand. */
  videoId: string | null;
  videoTitle: string;
  bookTitle: string | null;
  subjectName: string | null;
  gradeName: string | null;
  language: Language | null;
  quality: Quality | null;
  total: number;
  views: number;
  downloads: number;
  schoolCount: number;
  schools: UsageContentSchool[];
}

export interface UsageContent {
  windowDays: number | null;
  items: UsageContentItem[];
}

export interface UsageSchoolUser {
  id: string;
  email: string;
  role: Role;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
  locked: boolean;
  /** Who created the account — the delegation chain. */
  createdBy: { id: string; email: string } | null;
  scopeCount: number;
}

export interface UsageSchoolDetail {
  windowDays: number | null;
  school: {
    id: string;
    name: string;
    contactName: string | null;
    phone: string | null;
    email: string | null;
    status: SchoolStatus;
    licenseEnd: string | null;
    daysToExpiry: number | null;
    seatLimit: number | null;
    createdAt: string;
  };
  seatsUsed: number;
  lastActivityAt: string | null;
  entitlements: AdminEntitlement[];
  users: UsageSchoolUser[];
  activity: {
    views: number;
    downloads: number;
    top: {
      videoFileId: string;
      videoId: string | null;
      videoTitle: string;
      bookTitle: string | null;
      subjectName: string | null;
      gradeName: string | null;
      language: Language | null;
      quality: Quality | null;
      count: number;
    }[];
  };
}

// ---- School admin (Phase 3) -------------------------------------------------

export type UserStatus = 'ACTIVE' | 'SUSPENDED';
/** The only roles a school admin may assign. SCHOOL_ADMIN is the super admin's to grant. */
export type ManageableRole = 'COORDINATOR' | 'TEACHER';

export interface SchoolUser {
  id: string;
  email: string;
  role: Role;
  status: UserStatus;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  createdAt: string;
  /** The coordinator who created this teacher, if any. */
  createdById: string | null;
}

export interface SchoolUsersResponse {
  users: SchoolUser[];
  schoolName: string | null;
  /** null = uncapped. */
  seatLimit: number | null;
  seatsUsed: number;
}

// ---- Scopes (Phase 4) -------------------------------------------------------

/** One grant to send. subjectId absent/null = every subject in the grade. */
export interface ScopeEntry {
  gradeId: string;
  subjectId?: string | null;
  canDownload?: boolean;
}

/** A stored grant, resolved with names for display. */
export interface ResolvedScopeRow {
  gradeId: string;
  gradeName: string;
  subjectId: string | null;
  subjectName: string | null;
  canDownload: boolean;
}

/**
 * The tree a granter may grant FROM — the school's entitlements for a school
 * admin, the coordinator's own scope for a coordinator. The picker renders
 * exactly this and nothing wider.
 */
export interface GrantableGrade {
  id: string;
  name: string;
  order: number;
  /** The whole grade may be granted as one (not merely every subject in it). */
  whole: boolean;
  canDownload: boolean;
  subjects: { id: string; name: string; order: number; canDownload: boolean }[];
}

export interface CoordinatorTeachersResponse {
  teachers: SchoolUser[];
  schoolName: string | null;
  seatLimit: number | null;
  seatsUsed: number;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * The server's machine-readable refusal code, when it sent one
     * (`current_password_wrong`, `email_taken`, …). Pages switch on this rather
     * than on the message, which is prose and translated. Undefined for the
     * many endpoints that answer with a message alone.
     */
    public code?: string,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export const auth = {
  get token() {
    return accessToken;
  },
  set(t: string | null) {
    accessToken = t;
  },
};

async function raw(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(path, { ...init, headers, credentials: 'same-origin' });
}

/**
 * Refresh is deduped: a burst of parallel 401s (the book page fires several
 * requests at once) must produce exactly one /auth/refresh call, or the later
 * ones race and rotate the cookie out from under the earlier ones.
 */
async function refresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'same-origin',
        });
        if (!res.ok) return false;
        const body = (await res.json()) as { accessToken: string };
        accessToken = body.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        // Cleared on the next tick so concurrent callers all observe this run.
        setTimeout(() => (refreshInFlight = null), 0);
      }
    })();
  }
  return refreshInFlight;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await raw(path, init);

  if (res.status === 401 && (await refresh())) {
    res = await raw(path, init);
  }

  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    try {
      const body = (await res.json()) as { message?: string | string[]; code?: string };
      if (body.message) msg = Array.isArray(body.message) ? body.message.join(', ') : body.message;
      // Carried through so callers can switch on the code and fall back to the
      // server's own message when they do not recognise it.
      code = body.code;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg, code);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Query string from defined values only. Undefined entries are dropped rather
 * than sent as "undefined" — the usage DTOs run forbidNonWhitelisted, so a
 * stray param is a 400, and `days=undefined` would fail @IsInt anyway.
 */
function qs(params: Record<string, number | string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const api = {
  async login(email: string, password: string, turnstileToken?: string): Promise<Me> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      // Omitted rather than sent as undefined/null: the DTO accepts a string or
      // nothing, and forbidNonWhitelisted would 400 on a null.
      body: JSON.stringify(turnstileToken ? { email, password, turnstileToken } : { email, password }),
    });
    if (!res.ok) {
      // 403 carries a `code` (account_locked | account_suspended |
      // school_suspended | school_expired) so the page can say which, and 400
      // carries captcha_failed. 401 is always the generic
      // wrong-email-or-password case.
      let code = 'failed';
      if (res.status === 403 || res.status === 400) {
        const body = (await res.json().catch(() => null)) as {
          code?: string;
        } | null;
        // A 400 without a code is a validation error, not a captcha failure.
        code = body?.code ?? (res.status === 403 ? 'account_locked' : 'failed');
      }
      throw new ApiError(res.status, code);
    }
    const body = (await res.json()) as { accessToken: string; user: Me };
    accessToken = body.accessToken;
    return body.user;
  },

  /**
   * Phase 6 step 1. Requests a reset link.
   *
   * Resolves for an unknown address exactly as it does for a real one — the
   * server answers identically by design, and the page must not invent a
   * difference the server refused to make. Only a transport failure or the
   * captcha rejection rejects.
   */
  async forgotPassword(email: string, turnstileToken?: string): Promise<void> {
    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(turnstileToken ? { email, turnstileToken } : { email }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { code?: string } | null;
      // 400 captcha_failed, 429 too many requests, or a validation 400.
      throw new ApiError(res.status, body?.code ?? (res.status === 429 ? 'rate_limited' : 'failed'));
    }
  },

  /** Phase 6 step 2. Consumes the emailed token and sets the new password. */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as {
        code?: string;
        message?: string | string[];
      } | null;
      // reset_token_invalid is the server's one generic token failure. A 400
      // with no code is DTO validation — in practice the password policy.
      const code =
        body?.code ?? (res.status === 400 ? 'weak_password' : res.status === 429 ? 'rate_limited' : 'failed');
      throw new ApiError(res.status, code);
    }
  },

  /** Restores a session after reload using the refresh cookie alone. */
  async bootstrap(): Promise<boolean> {
    return refresh();
  },

  /**
   * Phase 8. The signed-in user's own credentials.
   *
   * Both go through req(), so they carry the bearer token and inherit the
   * 401-refresh-retry. Both bump tokenVersion server-side, which invalidates
   * every OTHER session; the response also re-issues this browser's cookies, so
   * the caller stays signed in. See Account.tsx for the refresh that confirms
   * it rather than assuming it.
   */
  account: {
    changePassword: (currentPassword: string, newPassword: string) =>
      req<void>('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      }),

    changeEmail: (currentPassword: string, newEmail: string) =>
      req<{ email: string }>('/api/auth/change-email', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newEmail }),
      }),
  },

  async logout(): Promise<void> {
    accessToken = null;
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
  },

  catalog: () => req<CatalogGrade[]>('/api/catalog'),
  book: (id: string) => req<BookDetail>(`/api/books/${id}`),
  video: (id: string) => req<VideoDetail>(`/api/videos/${id}`),

  /**
   * Mint a signed media URL. Called lazily — one per lesson, at the moment it
   * is needed (spec §7: never pre-mint a whole book's links).
   */
  access: (videoId: string, language: Language, quality: Quality, kind: 'stream' | 'download') =>
    req<{ url: string; videoFileId: string; sizeBytes: string }>(
      `/api/videos/${videoId}/access`,
      { method: 'POST', body: JSON.stringify({ language, quality, kind }) },
    ),

  /**
   * Super-admin only. Every route below 403s for any other role — the UI guard
   * is a convenience, the server is the actual boundary.
   */
  admin: {
    schools: () => req<AdminSchool[]>('/api/admin/schools'),

    school: async (id: string): Promise<AdminSchool> => {
      // No GET-one endpoint; the list is small and already authoritative.
      const all = await req<AdminSchool[]>('/api/admin/schools');
      const hit = all.find((s) => s.id === id);
      if (!hit) throw new ApiError(404, 'No such school');
      return hit;
    },

    createSchool: (input: CreateSchoolInput) =>
      req<{ id: string; name: string }>('/api/admin/schools', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    updateSchool: (id: string, patch: Partial<CreateSchoolInput> & { status?: SchoolStatus }) =>
      req<AdminSchool>(`/api/admin/schools/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    entitlements: (id: string) =>
      req<AdminEntitlement[]>(`/api/admin/schools/${id}/entitlements`),

    setEntitlements: (id: string, entitlements: EntitlementEntry[]) =>
      req<{ ok: true; count: number }>(`/api/admin/schools/${id}/entitlements`, {
        method: 'PUT',
        body: JSON.stringify({ entitlements }),
      }),

    grades: () => req<AdminGrade[]>('/api/admin/grades'),

    /**
     * Phase 7 usage views. Cross-school aggregates, so SUPER_ADMIN only —
     * every one of these 403s for any other role, which is the boundary that
     * counts; the route guard in main.tsx merely hides the screen.
     *
     * `days` omitted = all time.
     */
    usage: {
      overview: (days?: number) =>
        req<UsageOverview>(`/api/admin/usage/overview${qs({ days })}`),

      content: (days?: number, limit?: number) =>
        req<UsageContent>(`/api/admin/usage/content${qs({ days, limit })}`),

      school: (id: string, days?: number) =>
        req<UsageSchoolDetail>(`/api/admin/schools/${id}/usage${qs({ days })}`),
    },
  },

  /**
   * School-admin only. Note that no call here names a school — the server
   * derives it from the token, so there is no id a client could tamper with.
   */
  school: {
    users: () => req<SchoolUsersResponse>('/api/school/users'),

    createUser: (input: {
      email: string;
      password: string;
      role: ManageableRole;
      scope?: ScopeEntry[];
    }) => req<SchoolUser>('/api/school/users', { method: 'POST', body: JSON.stringify(input) }),

    updateUser: (id: string, patch: { status?: UserStatus; role?: ManageableRole }) =>
      req<SchoolUser>(`/api/school/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    resetPassword: (id: string, password: string) =>
      req<{ ok: true }>(`/api/school/users/${id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    deleteUser: (id: string) =>
      req<{ ok: true }>(`/api/school/users/${id}`, { method: 'DELETE' }),

    /** What this school admin may grant: the school's entitlements. */
    grantable: () => req<GrantableGrade[]>('/api/school/grantable'),

    scope: (id: string) => req<ResolvedScopeRow[]>(`/api/school/users/${id}/scope`),

    setScope: (id: string, scope: ScopeEntry[]) =>
      req<ResolvedScopeRow[]>(`/api/school/users/${id}/scope`, {
        method: 'PUT',
        body: JSON.stringify({ scope }),
      }),
  },

  /**
   * Coordinator only. Reaches teachers this coordinator created, and nothing
   * else — no call here can name another coordinator's teacher, and there is no
   * role parameter anywhere, by design.
   */
  coordinator: {
    teachers: () => req<CoordinatorTeachersResponse>('/api/coordinator/teachers'),

    /** What this coordinator may grant: their OWN scope, not the school's. */
    grantable: () => req<GrantableGrade[]>('/api/coordinator/grantable'),

    ownScope: () => req<ResolvedScopeRow[]>('/api/coordinator/scope'),

    createTeacher: (input: { email: string; password: string; scope?: ScopeEntry[] }) =>
      req<SchoolUser>('/api/coordinator/teachers', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    updateTeacher: (id: string, patch: { status?: UserStatus }) =>
      req<SchoolUser>(`/api/coordinator/teachers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    scope: (id: string) => req<ResolvedScopeRow[]>(`/api/coordinator/teachers/${id}/scope`),

    setScope: (id: string, scope: ScopeEntry[]) =>
      req<ResolvedScopeRow[]>(`/api/coordinator/teachers/${id}/scope`, {
        method: 'PUT',
        body: JSON.stringify({ scope }),
      }),

    resetPassword: (id: string, password: string) =>
      req<{ ok: true }>(`/api/coordinator/teachers/${id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),

    deleteTeacher: (id: string) =>
      req<{ ok: true }>(`/api/coordinator/teachers/${id}`, { method: 'DELETE' }),
  },
};
